import { ref } from 'vue'
import { getConfiguredFieldOrder } from '@/config/zeekFields'

const STORAGE_KEY = 'eventSearch.fieldSettings'
const OLD_STORAGE_KEY = 'eventSearch.disabledFields'

// Shared state across all component instances
const fieldSettings = ref(loadFromStorage())

/**
 * Load settings from localStorage, with migration from old format
 */
function loadFromStorage() {
  try {
    // Try to load new format first
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }

    // Migrate from old format if exists
    const oldStored = localStorage.getItem(OLD_STORAGE_KEY)
    if (oldStored) {
      const oldData = JSON.parse(oldStored)
      const migrated = {}

      // Convert old format { logType: [disabled fields] } to new format
      for (const [logType, disabledFields] of Object.entries(oldData)) {
        migrated[logType] = {
          order: null, // No custom order
          disabled: disabledFields
        }
      }

      // Save in new format and remove old key
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated))
      localStorage.removeItem(OLD_STORAGE_KEY)

      return migrated
    }

    return {}
  } catch {
    return {}
  }
}

/**
 * Save settings to localStorage
 */
function saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fieldSettings.value))
  } catch {
    // Ignore storage errors
  }
}

/**
 * Ensure log type entry exists in settings
 */
function ensureLogType(logType) {
  if (!fieldSettings.value[logType]) {
    fieldSettings.value[logType] = {
      order: null,
      disabled: []
    }
  }
}

export function useFieldSettings() {
  /**
   * Check if a field is enabled for a given log type
   * @param {string} logType - The log type (conn, dns, http, etc.)
   * @param {string} field - The field name
   * @returns {boolean} True if the field is enabled
   */
  function isFieldEnabled(logType, field) {
    const settings = fieldSettings.value[logType]
    if (!settings || !settings.disabled || !Array.isArray(settings.disabled)) {
      return true
    }
    return !settings.disabled.includes(field)
  }

  /**
   * Toggle a field's visibility for a given log type
   * @param {string} logType - The log type
   * @param {string} field - The field name
   */
  function toggleField(logType, field) {
    ensureLogType(logType)

    const disabled = fieldSettings.value[logType].disabled || []
    const index = disabled.indexOf(field)

    if (index === -1) {
      // Field is enabled, disable it
      fieldSettings.value[logType].disabled = [...disabled, field]
    } else {
      // Field is disabled, enable it
      fieldSettings.value[logType].disabled = disabled.filter((_, i) => i !== index)
    }

    saveToStorage()
  }

  /**
   * Extract field name from either a string or FieldInfo object
   * @param {string|{name: string, type: string}} field - Field or FieldInfo
   * @returns {string} The field name
   */
  function getFieldName(field) {
    if (typeof field === 'object' && field !== null && field.name) {
      return field.name
    }
    return field
  }

  /**
   * Get the field order for a log type
   * Default order: config fields first (in config order), then remaining API fields alphabetically
   * @param {string} logType - The log type
   * @param {Array<string|{name: string}>} apiFields - Available fields from API (string or FieldInfo objects)
   * @returns {string[]} Ordered field names
   */
  function getFieldOrder(logType, apiFields) {
    if (!apiFields || !Array.isArray(apiFields)) {
      return []
    }

    // Normalize to field names (handle both string[] and FieldInfo[] formats)
    const apiFieldNames = apiFields.map(getFieldName)

    const settings = fieldSettings.value[logType]
    const customOrder = settings?.order

    // Determine the base order to use
    let baseOrder
    if (customOrder && Array.isArray(customOrder) && customOrder.length > 0) {
      // User has customized the order
      baseOrder = customOrder
    } else {
      // Use config order as default: config fields first, then others alphabetically
      const configOrder = getConfiguredFieldOrder(logType)
      const apiSet = new Set(apiFieldNames)
      const configFields = configOrder.filter(f => apiSet.has(f))
      const otherFields = apiFieldNames.filter(f => !configOrder.includes(f)).sort()

      // For unknown log types (no config), ensure 'ts' is always first
      if (configFields.length === 0 && apiSet.has('ts')) {
        const tsIndex = otherFields.indexOf('ts')
        if (tsIndex > 0) {
          otherFields.splice(tsIndex, 1)
          otherFields.unshift('ts')
        }
      }

      baseOrder = [...configFields, ...otherFields]
    }

    // Ensure all API fields are included (base order may have old/missing fields)
    const apiSet = new Set(apiFieldNames)
    const result = []

    // Add fields from base order that exist in API
    for (const field of baseOrder) {
      if (apiSet.has(field)) {
        result.push(field)
        apiSet.delete(field)
      }
    }

    // Add any new API fields not in base order (alphabetically)
    const remaining = [...apiSet].sort()
    result.push(...remaining)

    return result
  }

  /**
   * Set custom field order for a log type
   * @param {string} logType - The log type
   * @param {string[]} orderedFields - Fields in desired order
   */
  function setFieldOrder(logType, orderedFields) {
    ensureLogType(logType)
    fieldSettings.value[logType].order = [...orderedFields]
    saveToStorage()
  }

  /**
   * Get enabled fields in the correct order
   * @param {string} logType - The log type
   * @param {Array<string|{name: string}>} allFields - All available fields from API (string or FieldInfo objects)
   * @returns {string[]} Ordered and enabled field names
   */
  function getOrderedEnabledFields(logType, allFields) {
    if (!allFields || !Array.isArray(allFields)) {
      return []
    }

    const orderedFields = getFieldOrder(logType, allFields)
    return orderedFields.filter(field => isFieldEnabled(logType, field))
  }

  /**
   * Reset all settings for a log type (order and visibility)
   * @param {string} logType - The log type
   */
  function resetLogType(logType) {
    if (fieldSettings.value[logType]) {
      delete fieldSettings.value[logType]
      saveToStorage()
    }
  }

  /**
   * Check if a log type has any custom settings
   * @param {string} logType - The log type
   * @returns {boolean} True if log type has custom order or disabled fields
   */
  function hasCustomSettings(logType) {
    const settings = fieldSettings.value[logType]
    if (!settings) return false
    const hasCustomOrder = settings.order && Array.isArray(settings.order) && settings.order.length > 0
    const hasDisabledFields = settings.disabled && Array.isArray(settings.disabled) && settings.disabled.length > 0
    return hasCustomOrder || hasDisabledFields
  }

  return {
    isFieldEnabled,
    toggleField,
    getFieldOrder,
    setFieldOrder,
    getOrderedEnabledFields,
    resetLogType,
    hasCustomSettings
  }
}
